import type { Order, SmartpayTerminal, BusinessInfo } from '../types';
import { logger } from './logger';
import { config } from './config';

// SmartConnect API Configuration
const DEFAULT_API_HOST = 'https://api.smart-connect.cloud/POS';
const DEV_API_HOST = 'https://api-dev.smart-connect.cloud/POS';

export enum TransactionOutcome {
  Accepted = 1,      // TransactionResult = "OK-ACCEPTED"
  Declined = 2,      // TransactionResult = "OK-DECLINED"
  Cancelled = 3,    // TransactionResult = "CANCELLED", Result != "FAILED-INTERFACE"
  DeviceOffline = 4, // TransactionResult = "CANCELLED", Result = "FAILED-INTERFACE"
  Failed = 5        // Everything else
}

export interface TransactionResult {
  success: boolean;
  message: string;
  transactionId?: string;
  responseData?: any;
  outcome?: TransactionOutcome;
}

export interface PairingResult {
  success: boolean;
  message: string;
}

export interface TerminalStatus {
  isOnline: boolean;
  isReady: boolean;
  statusMessage: string;
  lastStatusCheck?: number;
}

export interface SaleData {
  version: string;
  saleData: {
    saleId?: string;
    invoiceNumber?: string;
    createdAt?: string;
    updatedAt?: string;
    totalAmount: string;
    totalTax: string;
    totalTips?: string;
    totalSurcharge?: string;
    returnFor?: string;
    userId?: string;
    userName?: string;
    customerId?: string;
    customerName?: string;
    lineItems?: Array<{
      lineId?: string;
      sequenceNumber?: string;
      productId?: string;
      productName: string;
      productDescription?: string;
      categories?: Array<{
        categoryId?: string;
        categoryName: string;
        departmentId?: string;
        departmentName?: string;
      }>;
      brandId?: string;
      brandName?: string;
      quantity: string;
      unitPrice: string;
      unitTax: string;
      unitDiscount?: string;
      totalPrice: string;
      totalTax: string;
      totalDiscount?: string;
      modifierFor?: string;
      skuCode?: string;
      barcode?: string;
    }>;
  };
}

/**
 * SmartConnect API Service
 * Handles all communication with SmartConnect cloud API
 */
export class SmartConnectService {
  private apiHost: string;
  private posVendorName: string;
  private posBusinessName: string;

  constructor(
    apiHost: string = DEFAULT_API_HOST,
    posVendorName: string = 'KINGS PARK POS',
    posBusinessName: string = 'Kings Park Restaurant'
  ) {
    this.apiHost = apiHost;
    this.posVendorName = posVendorName;
    this.posBusinessName = posBusinessName;
  }

  /**
   * Pair a register with a payment terminal
   * Follows API developer pattern: PUT request, expects 200 status with no body
   */
  async pairTerminal(pairingCode: string, registerId: string, registerName: string): Promise<PairingResult> {
    if (!pairingCode) {
      return {
        success: false,
        message: 'A pairing code has to be supplied.',
      };
    }

    const url = `${this.apiHost}/Pairing/${pairingCode}`;
    const body = new URLSearchParams();
    body.append('POSRegisterID', registerId);
    body.append('POSRegisterName', registerName);
    body.append('POSBusinessName', this.posBusinessName);
    body.append('POSVendorName', this.posVendorName);

    try {
      // Log pairing request (sanitized)
      logger.debug('Sending pairing request', { 
        apiHost: this.apiHost,
        url: url.replace(/\/[^\/]+$/, '/[PAIRING_CODE]'), // Redact pairing code
        registerId,
        registerName,
        posBusinessName: this.posBusinessName,
        posVendorName: this.posVendorName,
      });

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        },
        body: body.toString(),
      });

      const responseText = await response.text();
      logger.debug('Pairing response received', { 
        status: response.status,
        success: response.status === 200 
      });

      if (response.status === 200) {
        // API developer pattern: 200 status means success, no body expected
        return {
          success: true,
          message: 'Successfully paired with the terminal.',
        };
      } else {
        // Try to parse error message from response
        let errorMessage = `Pairing failed with status ${response.status}.`;
        try {
          // Try to parse as JSON first
          const errorData = JSON.parse(responseText);
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch {
          // If not JSON, use response text if available
          if (responseText && responseText.trim()) {
            errorMessage = `${errorMessage} ${responseText}`;
          } else {
            // Provide more specific error messages based on status code
            switch (response.status) {
              case 400:
                errorMessage = 'Bad Request: Invalid pairing code or register information. Please check your pairing code and try again.';
                break;
              case 404:
                errorMessage = 'Terminal not found: The pairing code may be invalid or expired.';
                break;
              case 500:
                errorMessage = 'Server error: The SmartConnect service is temporarily unavailable. Please try again later.';
                break;
              default:
                errorMessage = `Pairing failed (Status ${response.status}). Please check your pairing code and try again.`;
            }
          }
        }
        logger.debug('Pairing failed', { status: response.status, responseText });
        return {
          success: false,
          message: errorMessage,
        };
      }
    } catch (error: any) {
      console.error('Error during pairing API call:', error);
      return {
        success: false,
        message: `Network error: ${error.message || 'Could not connect to SmartConnect'}`,
      };
    }
  }

  /**
   * Create a transaction and get polling URL
   * Follows API developer pattern: POST request returns polling URL for async transaction
   */
  private async createTransaction(
    registerId: string,
    transactionType: string,
    amount?: number,
    saleData?: SaleData,
    originalTransactionId?: string,
    date?: string
  ): Promise<string> {
    // Validate amount if provided
    if (amount !== undefined) {
      // Check if amount is a valid number
      if (typeof amount !== 'number' || isNaN(amount) || !isFinite(amount)) {
        throw new Error('The provided amount is not a valid number');
      }
      // Check if amount is zero - special handling for $0.00 payments
      if (amount === 0) {
        throw new Error('Payment amount cannot be $0.00. If the order is already paid, no payment is needed.');
      }
      // Check if amount is positive
      if (amount < 0) {
        throw new Error('The provided amount must be greater than zero');
      }
      // Check if amount can be converted to cents (max 2 decimal places)
      // Use a small epsilon to handle floating point precision issues
      const amountInCents = amount * 100;
      const roundedCents = Math.round(amountInCents);
      if (Math.abs(amountInCents - roundedCents) > 0.01) {
        throw new Error('The provided amount has too many decimal places (maximum 2 decimal places allowed)');
      }
    }
    if (!transactionType) {
      throw new Error('The transactionType has to be supplied');
    }

    const url = `${this.apiHost}/Transaction`;
    const body = new URLSearchParams();
    
    body.append('POSRegisterID', registerId);
    body.append('POSBusinessName', this.posBusinessName);
    body.append('POSVendorName', this.posVendorName);
    body.append('TransactionMode', 'ASYNC'); // API developer uses ASYNC mode
    body.append('TransactionType', transactionType);

    if (amount !== undefined) {
      // API developer pattern: AmountTotal in cents (e.g., 199 for $1.99)
      const amountInCents = Math.round(amount * 100);
      body.append('AmountTotal', amountInCents.toString());
    }

    if (originalTransactionId) {
      body.append('OriginalTransactionID', originalTransactionId);
    }

    if (date) {
      body.append('Date', date);
    }

    if (saleData) {
      body.append('saleData', JSON.stringify(saleData));
    }

    // Log transaction request (sanitized in production)
    logger.debug('Sending transaction POST request', { 
      url: url.replace(/\/[^\/]+$/, '/[REDACTED]'), // Redact endpoint details
      registerId,
      transactionType,
      amount: amount !== undefined ? Math.round(amount * 100) : undefined,
    });

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        },
        body: body.toString(),
      });

      const responseText = await response.text();
      // Log response status only, not full response (may contain sensitive data)
      logger.debug('Transaction POST response received', { 
        status: response.status,
        hasData: !!responseText 
      });

      if (response.status === 200) {
        const data = JSON.parse(responseText);
        if (data && data.data && data.data.PollingUrl) {
          return data.data.PollingUrl;
        } else {
          throw new Error('Returned 200 but Polling URL missing');
        }
      } else {
        let errorMessage = 'Invalid status code received';
        try {
          const errorData = JSON.parse(responseText);
          errorMessage = errorData.error || errorMessage;
        } catch {
          // If parsing fails, use default
        }
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      console.error('Error during transaction API call:', error);
      if (error.message) {
        throw error;
      }
      throw new Error(`Network error: ${error.message || 'Could not connect to SmartConnect'}`);
    }
  }

  /**
   * Poll for transaction outcome
   * Follows API developer pattern: Polls until transaction completes or timeout
   * Now supports transaction state tracking for power failure recovery
   */
  private async pollForOutcome(
    pollingUrl: string,
    onDelayed?: () => void,
    transactionStateId?: string
  ): Promise<{ outcome: TransactionOutcome; responseData: any }> {
    const interval = 2 * 1000; // 2 seconds (API developer pattern)
    const timeout = 10 * 60 * 1000; // 10 minutes (API developer pattern)
    const endTime = Date.now() + timeout;

    return new Promise((resolve, reject) => {
      if (!pollingUrl) {
        reject(new Error('Polling URL needs to be submitted'));
        return;
      }

      const checkCondition = async () => {
        try {
          logger.debug('Polling for transaction outcome');

          // Update transaction state to polling if tracking
          if (transactionStateId) {
            const { transactionStateManager } = await import('./transactionStateManager');
            const current = transactionStateManager.getPendingTransaction(transactionStateId);
            // Check if transaction was cancelled
            if (current?.status === 'cancelled') {
              reject(new Error('Transaction was cancelled'));
              return;
            }
            transactionStateManager.updatePendingTransaction(transactionStateId, {
              status: 'polling',
              lastPollTime: Date.now(),
            });
          }

          const response = await fetch(pollingUrl, {
            method: 'GET',
          });

          const responseText = await response.text();
          // Log response status only, not full response (may contain sensitive data)
          logger.debug('Transaction GET response received', { 
            status: response.status,
            hasData: !!responseText 
          });

          let transactionComplete = false;
          let transactionOutcome: TransactionOutcome | undefined;

          if (response.status === 200) {
            const data = JSON.parse(responseText);
            if (data && data.data) {
              const transactionStatus = data.transactionStatus;
              const transactionResult = data.data.TransactionResult;
              const result = data.data.Result;

              if (transactionStatus === 'COMPLETED') {
                transactionComplete = true;

                // Determine outcome based on API developer pattern
                if (transactionResult === 'OK-ACCEPTED') {
                  transactionOutcome = TransactionOutcome.Accepted;
                } else if (transactionResult === 'OK-DECLINED') {
                  transactionOutcome = TransactionOutcome.Declined;
                } else if (transactionResult === 'CANCELLED' && result !== 'FAILED-INTERFACE') {
                  transactionOutcome = TransactionOutcome.Cancelled;
                } else if (transactionResult === 'CANCELLED' && result === 'FAILED-INTERFACE') {
                  transactionOutcome = TransactionOutcome.DeviceOffline;
                } else {
                  transactionOutcome = TransactionOutcome.Failed;
                }
              } else if (transactionStatus === 'PENDING' && transactionResult === 'OK-DELAYED' && onDelayed) {
                // Transaction delayed - invoke callback
                onDelayed();
              }
            } else {
              reject(new Error('Returned 200 but data structure not as expected'));
              return;
            }
          } else {
            // Ignore failed requests and continue polling (API developer pattern)
            console.log('Ignoring failed request...');
          }

          if (transactionComplete && transactionOutcome !== undefined) {
            resolve({ outcome: transactionOutcome, responseData: JSON.parse(responseText) });
            return;
          } else if (Date.now() < endTime) {
            // Continue polling
            setTimeout(checkCondition, interval);
            return;
          } else {
            reject(new Error('Polling timed out'));
            return;
          }
        } catch (error: any) {
          reject(error);
        }
      };

      checkCondition();
    });
  }

  /**
   * Send a transaction request to SmartConnect (with polling)
   * This is the main transaction method that handles the full async flow
   * Now includes transaction state tracking for power failure recovery
   */
  private async sendTransaction(
    registerId: string,
    transactionType: string,
    amount?: number,
    saleData?: SaleData,
    originalTransactionId?: string,
    date?: string,
    onDelayed?: () => void,
    transactionStateId?: string // For tracking pending transactions
  ): Promise<TransactionResult> {
    try {
      // Step 1: Create transaction and get polling URL
      const pollingUrl = await this.createTransaction(
        registerId,
        transactionType,
        amount,
        saleData,
        originalTransactionId,
        date
      );

      // Update transaction state to polling if tracking
      if (transactionStateId) {
        const { transactionStateManager } = await import('./transactionStateManager');
        transactionStateManager.updatePendingTransaction(transactionStateId, {
          status: 'polling',
          pollingUrl,
        });
      }

      // Step 2: Poll for outcome
      const { outcome, responseData } = await this.pollForOutcome(pollingUrl, onDelayed, transactionStateId);

      // Extract transaction ID from multiple possible locations in the response
      // Smartpay API may return TransactionID in different locations
      const extractTransactionId = (data: any): string | undefined => {
        if (!data) return undefined;
        
        // Try multiple possible locations
        return (
          data.data?.TransactionID ||
          data.data?.transactionId ||
          data.data?.TransactionId ||
          data.TransactionID ||
          data.transactionId ||
          data.TransactionId ||
          data.data?.transaction_id ||
          data.transaction_id
        );
      };

      const smartpayTransactionId = extractTransactionId(responseData);
      
      // Log transaction ID extraction for debugging
      if (smartpayTransactionId) {
        logger.debug('Transaction ID extracted', { 
          transactionId: smartpayTransactionId,
          responseKeys: responseData ? Object.keys(responseData) : []
        });
      } else {
        logger.warn('Transaction ID not found in response', { 
          responseStructure: responseData ? JSON.stringify(responseData).substring(0, 200) : 'null'
        });
      }

      // Update transaction state if tracking
      if (transactionStateId) {
        const { transactionStateManager } = await import('./transactionStateManager');
        transactionStateManager.completeTransaction(
          transactionStateId,
          outcome,
          smartpayTransactionId
        );
      }

      // Step 3: Return result based on outcome
      const isSuccess = outcome === TransactionOutcome.Accepted;
      const messages = {
        [TransactionOutcome.Accepted]: 'Transaction accepted',
        [TransactionOutcome.Declined]: 'Transaction declined',
        [TransactionOutcome.Cancelled]: 'Transaction cancelled',
        [TransactionOutcome.DeviceOffline]: 'Device is offline',
        [TransactionOutcome.Failed]: 'Transaction failed',
      };

      return {
        success: isSuccess,
        message: messages[outcome],
        transactionId: smartpayTransactionId,
        responseData: responseData,
        outcome: outcome,
      };
    } catch (error: any) {
      console.error('Error during transaction:', error);
      
      // Mark transaction as failed if tracking
      if (transactionStateId) {
        const { transactionStateManager } = await import('./transactionStateManager');
        transactionStateManager.updatePendingTransaction(transactionStateId, {
          status: 'failed',
          outcome: TransactionOutcome.Failed,
        });
      }

      return {
        success: false,
        message: error.message || 'Transaction failed',
        outcome: TransactionOutcome.Failed,
      };
    }
  }

  /**
   * Initiate a Purchase transaction
   * Uses Card.Purchase transaction type (API developer pattern)
   */
  async purchase(
    registerId: string,
    amount: number,
    saleData?: SaleData,
    onDelayed?: () => void,
    transactionStateId?: string
  ): Promise<TransactionResult> {
    return this.sendTransaction(registerId, 'Card.Purchase', amount, saleData, undefined, undefined, onDelayed, transactionStateId);
  }

  /**
   * Recover a pending transaction by polling its URL
   * Used for power failure recovery scenarios
   */
  async recoverTransaction(pollingUrl: string, onDelayed?: () => void): Promise<TransactionResult> {
    try {
      // Poll for outcome without creating new transaction (no state tracking for recovery)
      const { outcome, responseData } = await this.pollForOutcome(pollingUrl, onDelayed, undefined);

      const isSuccess = outcome === TransactionOutcome.Accepted;
      const messages = {
        [TransactionOutcome.Accepted]: 'Transaction accepted',
        [TransactionOutcome.Declined]: 'Transaction declined',
        [TransactionOutcome.Cancelled]: 'Transaction cancelled',
        [TransactionOutcome.DeviceOffline]: 'Device is offline',
        [TransactionOutcome.Failed]: 'Transaction failed',
      };

      // Extract transaction ID from multiple possible locations
      const extractTransactionId = (data: any): string | undefined => {
        if (!data) return undefined;
        return (
          data.data?.TransactionID ||
          data.data?.transactionId ||
          data.data?.TransactionId ||
          data.TransactionID ||
          data.transactionId ||
          data.TransactionId ||
          data.data?.transaction_id ||
          data.transaction_id
        );
      };

      const smartpayTransactionId = extractTransactionId(responseData);

      return {
        success: isSuccess,
        message: messages[outcome],
        transactionId: smartpayTransactionId,
        responseData: responseData,
        outcome: outcome,
      };
    } catch (error: any) {
      console.error('Error recovering transaction:', error);
      return {
        success: false,
        message: error.message || 'Transaction recovery failed',
        outcome: TransactionOutcome.Failed,
      };
    }
  }

  /**
   * Initiate a Refund transaction
   * Uses Card.Refund transaction type (API developer pattern)
   */
  async refund(
    registerId: string,
    amount: number,
    originalTransactionId: string,
    saleData?: SaleData,
    onDelayed?: () => void
  ): Promise<TransactionResult> {
    return this.sendTransaction(
      registerId,
      'Card.Refund',
      amount,
      saleData,
      originalTransactionId,
      undefined,
      onDelayed
    );
  }

  /**
   * Get Terminal Status
   * Note: Status checks may be synchronous, but we'll use the async pattern for consistency
   */
  async getTerminalStatus(registerId: string): Promise<TerminalStatus> {
    try {
      const result = await this.sendTransaction(registerId, 'Terminal.GetStatus');
      return {
        isOnline: result.success,
        isReady: result.success && result.responseData?.status === 'ready',
        statusMessage: result.message,
        lastStatusCheck: Date.now(),
      };
    } catch (error: any) {
      return {
        isOnline: false,
        isReady: false,
        statusMessage: error.message || 'Failed to get terminal status',
        lastStatusCheck: Date.now(),
      };
    }
  }

  /**
   * Settlement Inquiry
   * Note: Settlement operations may need polling, using async pattern
   */
  async settlementInquiry(registerId: string, date?: string): Promise<TransactionResult> {
    const inquiryDate = date || new Date().toISOString().slice(0, 10).replace(/-/g, ''); // YYYYMMDD
    return this.sendTransaction(registerId, 'Acquirer.Settlement.Inquiry', undefined, undefined, undefined, inquiryDate);
  }

  /**
   * Settlement Cutover
   * Note: Settlement operations may need polling, using async pattern
   */
  async settlementCutover(registerId: string): Promise<TransactionResult> {
    return this.sendTransaction(registerId, 'Acquirer.Settlement.Cutover');
  }

  /**
   * Reprint Receipt
   * Note: Receipt operations may be synchronous, but using async pattern for consistency
   */
  async reprintReceipt(registerId: string): Promise<TransactionResult> {
    return this.sendTransaction(registerId, 'Journal.ReprintReceipt');
  }

  /**
   * Convert Order to SaleData format for SmartConnect
   */
  convertOrderToSaleData(
    order: Order,
    businessInfo?: BusinessInfo,
    userId?: string,
    userName?: string
  ): SaleData {
    const now = new Date().toISOString();
    const lineItems = order.items.map((item, index) => {
      const unitPrice = item.price / item.quantity;
      const unitTax = (order.taxAmount / order.totalAmount) * unitPrice;
      const totalPrice = item.price;
      const totalTax = (order.taxAmount / order.totalAmount) * totalPrice;

      return {
        lineId: `${order.id}-${index}`,
        sequenceNumber: String(index + 1),
        productId: String(item.id),
        productName: item.name,
        productDescription: item.notes,
        quantity: String(item.quantity),
        unitPrice: unitPrice.toFixed(2),
        unitTax: unitTax.toFixed(2),
        totalPrice: totalPrice.toFixed(2),
        totalTax: totalTax.toFixed(2),
        ...(item.selectedMeatOption && {
          modifierFor: `${order.id}-${index}`,
        }),
      };
    });

    return {
      version: '1.0.0',
      saleData: {
        saleId: order.id,
        invoiceNumber: order.id,
        createdAt: new Date(order.timestamp).toISOString(),
        updatedAt: now,
        totalAmount: order.totalAmount.toFixed(2),
        totalTax: order.taxAmount.toFixed(2),
        ...(order.discountAmount && {
          totalDiscount: order.discountAmount.toFixed(2),
        }),
        ...(userId && { userId: String(userId) }),
        ...(userName && { userName }),
        ...(order.customerId && { customerId: String(order.customerId) }),
        lineItems,
      },
    };
  }
}

// Export singleton instance
let smartConnectInstance: SmartConnectService | null = null;
let lastApiHost: string | undefined = undefined;
let lastPosVendorName: string | undefined = undefined;
let lastPosBusinessName: string | undefined = undefined;

export const getSmartConnectService = (
  apiHost?: string,
  posVendorName?: string,
  posBusinessName?: string
): SmartConnectService => {
  // Determine the API host to use
  // Priority: 1. Provided apiHost, 2. Config apiHost, 3. Dev API in dev mode, 4. Production API
  const effectiveApiHost = apiHost || config.smartconnect?.apiHost || (config.isDevelopment ? DEV_API_HOST : DEFAULT_API_HOST);
  
  // Recreate instance if parameters changed or if instance doesn't exist
  if (!smartConnectInstance || 
      lastApiHost !== effectiveApiHost || 
      lastPosVendorName !== posVendorName || 
      lastPosBusinessName !== posBusinessName) {
    logger.debug('Creating SmartConnectService', { 
      apiHost: effectiveApiHost,
      isDev: effectiveApiHost.includes('api-dev'),
      providedApiHost: apiHost || 'none',
      configApiHost: config.smartconnect?.apiHost || 'none',
      isDevelopment: config.isDevelopment,
      posVendorName: posVendorName || 'default',
      posBusinessName: posBusinessName || 'default'
    });
    smartConnectInstance = new SmartConnectService(effectiveApiHost, posVendorName, posBusinessName);
    lastApiHost = effectiveApiHost;
    lastPosVendorName = posVendorName;
    lastPosBusinessName = posBusinessName;
  }
  
  return smartConnectInstance;
};

// Legacy exports for backward compatibility
export const pairTerminal = async (
  pairingCode: string,
  registerId: string,
  registerName: string = 'Main Register',
  apiHost?: string,
  posVendorName?: string,
  posBusinessName?: string
): Promise<PairingResult> => {
  // Use configured API host or dev API in development mode if no apiHost is provided
  const { config } = await import('./config');
  const defaultApiHost = apiHost || config.smartconnect?.apiHost || (config.isDevelopment ? DEV_API_HOST : DEFAULT_API_HOST);
  const service = getSmartConnectService(defaultApiHost, posVendorName, posBusinessName);
  return service.pairTerminal(pairingCode, registerId, registerName);
};

export const sendPaymentToTerminal = async (
  terminal: SmartpayTerminal,
  amount: number,
  order?: Order,
  businessInfo?: BusinessInfo,
  userId?: string,
  userName?: string,
  onDelayed?: () => void,
  transactionStateId?: string
): Promise<TransactionResult> => {
  const service = getSmartConnectService();
  
  let saleData: SaleData | undefined;
  if (order) {
    saleData = service.convertOrderToSaleData(order, businessInfo, userId, userName);
  }

  return service.purchase(terminal.registerId, amount, saleData, onDelayed, transactionStateId);
};

